import { type D3DragEvent, drag, type ScaleLinear, scaleLinear, select } from "d3";
import { defaultsDeep } from "lodash-es";
import { debounce } from "lodash-es";
import { Component, createRef, type ReactElement } from "react";
import { CardRowSimple } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { getCssColor } from "../../app-utils/color.js";
import { selectedColorScheme } from "../../app-utils/genome-browser-color.js";
import { type Contig, type Range, type RegionFlag } from "../../graphql.js";
import { getProjectLabel } from "../../redux/modules/config.js";
import { TooltipController } from "../core/overlays/components/OverlayControllers.js";
import HorizontalAxis, { HorizontalAxisType } from "./HorizontalAxis.js";
import { type IBoxModel } from "./interfaces.js";

export interface IGraphicalRangeSelectorProps {
  range: Range; // [bp, bp]
  boxModel: IBoxModel;
  maxRange: number; // bp
  minRangeLength: number; // bp
  barHeight?: number; // pixels
  pointerWidth?: number; // pixels
  pointerHeight?: number; // pixels
  tickCount?: number; // the approximate number of ticks on the axis - d3 automatically chooses a reasonable decimal range
  axisSpace?: number; // pixels, the space between the axis and the pointers
  barClickStepFraction?: number; // fraction of the range width to step to the left or right
  onRangeChange?: (IRange) => void; // called for each drag event
  onRangeChangeMouseUp?: (IRange) => void; // called only once at the end of the drag operation
  flags?: RegionFlag[];
  contig?: Contig;
}

export interface IGraphicalRangeSelectorState {
  range: Range;
  grabbing: boolean;
  scale: ScaleLinear<number, number>;
}

/**
 * A slider with two draggable pointers that define a subrange in a range (domain).
 *
 * All coordinates are stored as base pairs (in domain space).
 * The slider is realized with an SVG element and d3Event handlers.
 * The d3 event handlers are attached to the corresponding DOM elements immediately after they haven been rendered.
 *
 * UI Functions:
 *  - the left and the right pointer of the selected range can be dragged with the mouse (resizing of selected range)
 *  - the selected range itself can be dragged with the mouse (repositioning of selected range)
 *  - a click on the bar repositions the center of the currently selected range to the click position
 *  - a click and drag outside of the currently selected range draws a transparent rectangle which becomes
 *    the new selected range as the mouse button is released (freehand selection).
 *
 * For freehand selection, the rectangle is painted directly using d3 functions without React knowing about it.
 * React is notified about the new selected range when the freehand selection has finished.
 * A reference to the selection shape is kept to perform manual clean-up of the selection shape.
 *
 * created on 11/23/2016
 */
class GraphicalRangeSelector extends Component<IGraphicalRangeSelectorProps, IGraphicalRangeSelectorState> {
  private static DEBOUNCE_TIMER = 300;

  private static defaultProps: IGraphicalRangeSelectorProps = {
    range: { start: 0, stop: 10 },
    boxModel: {
      padding: {
        left: 10,
        right: 10,
        top: 15,
        bottom: 0,
      },
      width: 170,
      height: 40,
    },
    maxRange: 10,
    minRangeLength: 1,
    barHeight: 15,
    pointerWidth: 7,
    pointerHeight: 22,
    barClickStepFraction: 0.618,
    tickCount: 5,
    axisSpace: 2,
  };

  /**
   * contentRef is the object in the svg that tightly encapsulates the bar and pointers(without margins);
   * The value of a d3Event.x is computed relative to this component (container)
   * thereby, the basepair position corresponding to this pixel value can be directly computed using scale.invert
   */
  private contentRef = createRef<SVGGElement>();

  private leftPointerRef = createRef<SVGGElement>();
  private rightPointerRef = createRef<SVGGElement>();
  private rangeRef = createRef<SVGRectElement>();
  private tooltipRef = createRef<any>();
  private dragStartOffset;
  private selectionStart;
  private selectionRange;
  private selectionShape;

  constructor(props: IGraphicalRangeSelectorProps) {
    super(props);

    this.handleLeftPointerDrag = this.handleLeftPointerDrag.bind(this);
    this.handleRightPointerDrag = this.handleRightPointerDrag.bind(this);

    this.handleRangeDragStart = this.handleRangeDragStart.bind(this);
    this.handleRangeDrag = this.handleRangeDrag.bind(this);
    this.handleRangeDragEnd = this.handleRangeDragEnd.bind(this);
    this.moveRange = this.moveRange.bind(this);

    this.handleContentClick = this.handleContentClick.bind(this);
    this.handleContentDragStart = this.handleContentDragStart.bind(this);
    this.handleContentDrag = this.handleContentDrag.bind(this);
    this.handleContentDragEnd = this.handleContentDragEnd.bind(this);

    this.registerD3EventHandlers = this.registerD3EventHandlers.bind(this);
    this.inCurrentRange = this.inCurrentRange.bind(this);
    this.updateStateAndNotify = this.updateStateAndNotify.bind(this);

    this.getColorStyle = this.getColorStyle.bind(this);

    this.state = {
      range: this.props.range,
      grabbing: false,
      scale: this.calculateScale(),
    };

    this.handleContentClick = debounce(this.handleContentClick, GraphicalRangeSelector.DEBOUNCE_TIMER);
  }

  componentDidUpdate(prevProps: IGraphicalRangeSelectorProps) {
    this.registerD3EventHandlers();

    if (prevProps.boxModel !== this.props.boxModel || prevProps.maxRange !== this.props.maxRange) {
      this.setState({ scale: this.calculateScale() });
    }

    // if change of range has been triggered externally
    if (prevProps.range !== this.props.range && this.props.range !== this.state.range) {
      this.setState({ range: this.props.range });
      this.tooltipRef?.current.updateTooltipPosition();
    }
  }

  componentDidMount() {
    this.registerD3EventHandlers();
    this.setState({ scale: this.calculateScale() });
  }

  calculateScale = () => {
    const sliderWidth =
      this.props.boxModel.width - (this.props.boxModel.padding.left + this.props.boxModel.padding.right);
    return scaleLinear().domain([0, this.props.maxRange]).range([0, sliderWidth]);
  };

  /**
   * Creates a state object, sets the state to it and notifies the external callback if any.
   */
  updateStateAndNotify(start: number, stop: number, grabbing: boolean): void {
    const newRange: Range = { start, stop };
    this.setState({ range: newRange, grabbing: grabbing });

    if (this.props.onRangeChange) {
      this.props.onRangeChange(newRange);
    }
  }

  flagOverview(flags: RegionFlag[], yPosition: number, height: number): ReactElement[] {
    return flags.map((flag: RegionFlag, count: number) => {
      const range: Range = flag.region.range;
      const x: number = this.state.scale(range.start);
      const width: number = Math.max(this.state.scale(range.stop + 1) - x, 1);
      const translateX = `translate(${x}, 0)`;
      const fillColor = getCssColor(flag.project.color);
      return (
        <rect
          style={{ fill: fillColor }}
          transform={translateX}
          x={0}
          y={yPosition}
          width={width}
          height={height}
          opacity={0.5}
          key={`flag-${flag.id}-${count}`}
        >
          <title>{`Genome Flag: ${flag.name}\n${getProjectLabel(true, false)}: ${flag.project.name}`}</title>
        </rect>
      );
    });
  }

  getColorStyle(className: string) {
    if (className === "selected") {
      return { fill: selectedColorScheme.medium };
    }

    return {};
  }

  render() {
    const props: IGraphicalRangeSelectorProps = this.props;

    const translate = `translate(${props.boxModel.padding.left},${props.boxModel.padding.top + props.axisSpace})`;
    // adding 0.5 to center/align lines (which have line-width = 1)
    const translateLeftPointer = `translate(${this.state.scale(this.state.range.start) + 0.5}, 0)`;
    const translateRightPointer = `translate(${this.state.scale(this.state.range.stop) + 0.5}, 0)`;

    const yPosition = (props.pointerHeight - props.barHeight) / 2;

    const tooltip = (
      <CardRowSimple
        value={`${formatNumberByThousands(this.props.range.stop - this.props.range.start + 1)} bp`}
        label={"Current zoom range"}
      />
    );

    return (
      <TooltipController
        ref={this.tooltipRef}
        content={tooltip}
        overlayOptions={{ placement: "bottom" }}
        targetRef={this.rangeRef}
      >
        {(tooltipTriggerProps) => (
          <div>
            <svg
              width={props.boxModel.width}
              height={props.boxModel.height}
              className="graphical-range-selector"
              onPointerEnter={tooltipTriggerProps.onPointerEnter}
              onPointerLeave={tooltipTriggerProps.onPointerLeave}
            >
              <HorizontalAxis
                boxModel={{
                  padding: {
                    left: props.boxModel.padding.left,
                    top: props.boxModel.padding.top + props.axisSpace,
                    right: props.boxModel.padding.right,
                  },
                  width: props.boxModel.width,
                }}
                type={HorizontalAxisType.TOP}
                tickCount={props.tickCount}
                domain={{
                  start: 0,
                  stop: props.maxRange,
                }}
              />
              <g transform={translate} ref={this.contentRef}>
                <rect
                  className="slider-bar"
                  y={yPosition}
                  width={props.boxModel.width - (props.boxModel.padding.left + props.boxModel.padding.right)}
                  height={props.barHeight}
                ></rect>
                <g className="features-overview">
                  {this.props.flags && this.flagOverview(this.props.flags, yPosition, props.barHeight)}
                </g>
                <rect
                  className={`slider-range ${this.state.grabbing ? "grabbing" : "grabbable"}`}
                  x={this.state.scale(this.state.range.start)}
                  y={yPosition}
                  width={this.state.scale(this.state.range.stop - this.state.range.start + 1) + 1}
                  height={props.barHeight}
                  ref={this.rangeRef}
                ></rect>
                <g
                  className="slider-pointer slider-pointer-left"
                  ref={this.leftPointerRef}
                  transform={translateLeftPointer}
                >
                  <rect x={-props.pointerWidth + 0.5} width={props.pointerWidth} height={props.pointerHeight} />
                  <line className="pointer-hairline" x1={0} y1={0} x2={0} y2={props.pointerHeight} />
                </g>
                <g
                  className="slider-pointer slider-pointer-right"
                  ref={this.rightPointerRef}
                  transform={translateRightPointer}
                >
                  <rect x={-0.5} width={props.pointerWidth} height={props.pointerHeight} />
                  <line className="pointer-hairline" x1={0} y1={0} x2={0} y2={props.pointerHeight} />
                </g>
              </g>
            </svg>
          </div>
        )}
      </TooltipController>
    );
  }

  /**
   * Registers d3 event handlers.
   * This happens AFTER the component has been rendered
   * Called in componentDidMount and componentDidUpdate (see below)
   */
  registerD3EventHandlers() {
    if (this.contentRef.current == null) {
      return;
    }

    select(this.contentRef.current).call(
      drag()
        .container(this.contentRef.current)
        .on("start", this.handleContentDragStart)
        .on("drag", this.handleContentDrag)
        .on("end", this.handleContentDragEnd),
    );

    if (this.leftPointerRef.current) {
      select(this.leftPointerRef.current).call(
        drag().container(this.contentRef.current).on("drag", this.handleLeftPointerDrag),
      );
    }

    if (this.rightPointerRef.current) {
      select(this.rightPointerRef.current).call(
        drag().container(this.contentRef.current).on("drag", this.handleRightPointerDrag),
      );
    }

    if (this.rangeRef.current) {
      select(this.rangeRef.current).call(
        drag()
          .container(this.contentRef.current)
          .on("start", this.handleRangeDragStart)
          .on("drag", this.handleRangeDrag)
          .on("end", this.handleRangeDragEnd),
      );
    }
  }

  /**
   * Handles the start of a new range selection if the click location is outside of the current range.
   */
  handleContentDragStart(event: D3DragEvent<any, any, any>) {
    const newContigPos = Math.round(this.state.scale.invert(event.x));
    if (!this.inCurrentRange(newContigPos)) {
      this.selectionStart = newContigPos;
    }
  }

  /**
   * Handles mouse dragging events of a new range selection.
   */
  handleContentDrag(event: D3DragEvent<any, any, any>) {
    if (this.selectionShape !== null && this.selectionShape !== undefined) {
      this.selectionShape.remove();
    }
    if (this.selectionStart < 1) {
      return;
    }

    const newContigPos = Math.round(this.state.scale.invert(event.x));
    const constrainedNewContigPos = Math.min(Math.max(1, newContigPos), this.props.maxRange);
    const relWidth = constrainedNewContigPos - this.selectionStart;
    if (relWidth < 0) {
      this.selectionShape = select(this.contentRef.current)
        .append("rect")
        .attr("class", "slider-selection")
        .attr("x", this.state.scale(this.selectionStart + relWidth))
        .attr("y", 0)
        .attr("width", this.state.scale(-relWidth + 1))
        .attr("height", this.props.pointerHeight);
      this.selectionRange = this.genStateObject(this.selectionStart + relWidth, this.selectionStart, false);
    } else {
      this.selectionShape = select(this.contentRef.current)
        .append("rect")
        .attr("class", "slider-selection")
        .attr("x", this.state.scale(this.selectionStart))
        .attr("y", 0)
        .attr("width", this.state.scale(relWidth + 1))
        .attr("height", this.props.pointerHeight);
      this.selectionRange = this.genStateObject(this.selectionStart, this.selectionStart + relWidth, false);
    }
  }

  /**
   * Handles the end of a new range selection and updates the state.
   */
  handleContentDragEnd(): void {
    if (this.selectionShape != null) {
      this.selectionShape.remove();
    }
    if (
      this.selectionRange &&
      this.selectionRange.range.stop - this.selectionRange.range.start + 1 >= this.props.minRangeLength
    ) {
      this.updateStateAndNotify(this.selectionRange.range.start, this.selectionRange.range.stop, false);
    } else {
      this.handleContentClick(this.selectionStart);
    }
    this.selectionStart = -1;
    this.selectionRange = null;
  }

  handleLeftPointerDrag(event: D3DragEvent<any, any, any>): void {
    const newContigPos = Math.round(this.state.scale.invert(event.x));
    const constrainedNewContigPos = Math.max(
      1,
      Math.min(this.state.range.stop - this.props.minRangeLength, newContigPos),
    );
    this.updateStateAndNotify(constrainedNewContigPos, this.state.range.stop, false);
  }

  handleRightPointerDrag(event: D3DragEvent<any, any, any>): void {
    const newPos = Math.round(this.state.scale.invert(event.x));
    const constrainedNewContigPos = Math.min(
      Math.max(this.state.range.start + this.props.minRangeLength, newPos),
      this.props.maxRange,
    );
    this.updateStateAndNotify(this.state.range.start, constrainedNewContigPos, false);
  }

  /**
   * Handles the start of dragging the entire range.
   */
  handleRangeDragStart(event: D3DragEvent<any, any, any>): void {
    this.dragStartOffset = Math.round(Math.max(1, this.state.scale.invert(event.x) - this.state.range.start));
  }

  /**
   * Moves the range by dragging with the mouse.
   */
  handleRangeDrag(event: D3DragEvent<any, any, any>): void {
    const newPos = Math.round(this.state.scale.invert(event.x)) - this.dragStartOffset;
    this.moveRange(newPos, true);
  }

  handleRangeDragEnd(): void {
    this.updateStateAndNotify(this.state.range.start, this.state.range.stop, false);
  }

  /**
   * Move the range directly to a different position via single click.
   * @param contigPos the new center position of the range (constrained by left and right boundary)
   */
  handleContentClick(contigPos): void {
    const w = this.state.range.stop - this.state.range.start + 1;
    const start = Math.round(contigPos - w / 2);
    this.moveRange(start, false);
  }

  /**
   * Moves the range to a different position (used by event handlers).
   * @param newContigPos the new center position of the range (constrained by left and right boundary)
   * @param grabbing
   */
  moveRange(newContigPos, grabbing: boolean): void {
    const w = this.state.range.stop - this.state.range.start;
    const boundedNewPos = Math.min(Math.max(1, newContigPos), this.props.maxRange - w);
    this.updateStateAndNotify(boundedNewPos, boundedNewPos + w, grabbing);
  }

  /**
   * Checks if contigPos is in the currently selected range in domain space.
   * @param contigPos
   * @return {boolean}
   */
  inCurrentRange(contigPos: number): boolean {
    return this.state.range.start <= contigPos && this.state.range.stop >= contigPos;
  }

  genStateObject(start: number, stop: number, grabbing: boolean): Partial<IGraphicalRangeSelectorState> {
    return {
      range: {
        start,
        stop,
      },
      grabbing,
    };
  }

  public static getPropsWithDefaults(propsWithoutDefaults: IGraphicalRangeSelectorProps): IGraphicalRangeSelectorProps {
    return defaultsDeep(propsWithoutDefaults, GraphicalRangeSelector.defaultProps) as IGraphicalRangeSelectorProps;
  }
}

export { GraphicalRangeSelector };
